import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "reactstrap";
import { verifyData } from "../../pages/Common/CommonFunctions/commonFunctions";

const processNumericLinks = (content) => {
  if (!React.isValidElement(content)) return content;

  const isClickable = (type) => {
    if (typeof type === "string") return type === "a" || type === "button";
    return type && (type.name === "Link" || type.displayName === "Link");
  };

  const traverse = (node) => {
    if (!React.isValidElement(node)) return node;

    if (isClickable(node.type)) {
      const children = node.props.children;
      if (typeof children === "string" || typeof children === "number") {
        const numStr = String(children).trim();
        if (numStr !== "" && !isNaN(numStr)) {
          const num = Number(numStr);
          if (num === 0) {
            return <div className="text-center">{0}</div>;
          } else {
            const existingClass = node.props.className || "";
            const targetClasses =
              "btn btn-sm btn-outline-primary ht-table-buttons";
            let newClass = existingClass;
            targetClasses.split(" ").forEach((cls) => {
              if (!newClass.includes(cls)) {
                newClass += ` ${cls}`;
              }
            });
            return React.cloneElement(node, {
              className: newClass.trim(),
            });
          }
        }
      }
    }

    if (node.props && node.props.children) {
      const processedChildren = React.Children.map(
        node.props.children,
        (child) => traverse(child),
      );
      return React.cloneElement(node, { children: processedChildren });
    }

    return node;
  };

  return traverse(content);
};

const getActionCount = (node) => {
  if (Array.isArray(node)) {
    return node.reduce((total, child) => total + getActionCount(child), 0);
  }

  if (!React.isValidElement(node)) {
    return 0;
  }

  if (
    node.props?.className &&
    typeof node.props.className === "string" &&
    node.props.className.includes("candidate-action-items")
  ) {
    return React.Children.toArray(node.props.children).reduce(
      (total, child) => total + getActionCount(child),
      0,
    );
  }

  const isActionElement =
    node.type === "button" ||
    node.type === "a" ||
    (node.type === "i" && node.props?.onClick) ||
    (typeof node.type === "function" && node.type.name === "Link") ||
    (typeof node.type === "object" && node.type?.displayName === "Link") ||
    (typeof node.props?.className === "string" &&
      node.props.className.includes("action-item"));

  if (isActionElement) {
    return 1;
  }

  return React.Children.toArray(node.props?.children || []).reduce(
    (total, child) => total + getActionCount(child),
    0,
  );
};

const enhanceActionElements = (
  node,
  inheritedTitle = "",
  isDropdown = false,
  onAction,
) => {
  if (!React.isValidElement(node)) return node;

  let currentTitle = inheritedTitle;
  if (node.props && node.props.title && typeof node.props.title === "string") {
    currentTitle = node.props.title;
  }

  if (
    node.type === "button" ||
    node.type === "a" ||
    (typeof node.type === "function" && node.type.name === "Link") ||
    (typeof node.type === "object" &&
      node.type &&
      node.type.displayName === "Link")
  ) {
    let hasText = false;
    let iconClass = "";

    React.Children.forEach(node.props.children, (child) => {
      if (typeof child === "string" && child.trim() !== "") hasText = true;
      if (
        React.isValidElement(child) &&
        child.type === "span" &&
        child.props.className &&
        child.props.className.includes("action-text")
      )
        hasText = true;
      if (
        React.isValidElement(child) &&
        child.type === "i" &&
        child.props.className
      )
        iconClass = child.props.className;
    });

    if (!hasText && iconClass) {
      let text = node.props["aria-label"] || currentTitle || "";
      if (!text) {
        if (iconClass.includes("eye")) text = "View";
        else if (iconClass.includes("edit")) text = "Edit";
        else if (iconClass.includes("trash")) text = "Delete";
        else if (iconClass.includes("download")) text = "Download";
        else if (iconClass.includes("check")) text = "Approve";
        else if (iconClass.includes("times")) text = "Reject";
        else if (iconClass.includes("user")) text = "Profile";
        else if (iconClass.includes("envelope")) text = "Email";
      }

      if (text) {
        if (isDropdown) {
          return React.cloneElement(
            node,
            {},
            ...React.Children.toArray(node.props.children),
            " ",
            <span className="action-text">{text}</span>,
          );
        } else {
          return React.cloneElement(
            node,
            {},
            ...React.Children.toArray(node.props.children),
            " ",
            <span className="action-text">{text}</span>,
          );
        }
      }
    }
  }

  if (node.type === "i" && node.props && node.props.onClick) {
    let iconClass = node.props.className || "";
    let text = node.props["aria-label"] || currentTitle || "";
    if (!text) {
      if (iconClass.includes("eye")) text = "View";
      else if (iconClass.includes("edit")) text = "Edit";
      else if (iconClass.includes("trash")) text = "Delete";
      else if (iconClass.includes("download")) text = "Download";
      else if (iconClass.includes("check")) text = "Approve";
      else if (iconClass.includes("times")) text = "Reject";
      else if (iconClass.includes("user")) text = "Profile";
      else if (iconClass.includes("envelope")) text = "Email";
    }

    if (text) {
      if (isDropdown) {
        return (
          <div
            onClick={(event) => {
              event.stopPropagation();
              onAction?.();
              node.props.onClick?.(event);
            }}
            className="table-action-menu-item"
            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
            title={node.props.title || currentTitle}
          >
            {React.cloneElement(node, { onClick: undefined })}
            <span className="action-text">{text}</span>
          </div>
        );
      } else {
        return (
          <button
            onClick={node.props.onClick}
            className="table-action-toggle"
            title={node.props.title || currentTitle}
          >
            {React.cloneElement(node, { onClick: undefined })}
            <span className="action-text">{text}</span>
          </button>
        );
      }
    }
  }

  if (node.props && node.props.children) {
    const processedChildren = React.Children.map(node.props.children, (child) =>
      enhanceActionElements(child, currentTitle, isDropdown, onAction),
    );
    return React.cloneElement(node, {}, processedChildren);
  }

  return node;
};

const ActionMenuPortal = ({ isOpen, anchorRef, onClose, children }) => {
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const button = anchorRef?.current;

      if (!button) {
        return;
      }

      const rect = button.getBoundingClientRect();
      const menuWidth = 160;
      const menuHeight = 90;
      const gap = 4;

      const openAbove = rect.bottom + menuHeight > window.innerHeight;

      const top = openAbove ? rect.top - menuHeight - gap : rect.bottom + gap;

      const left = Math.max(
        8,
        Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8),
      );

      setMenuPosition({
        top,
        left,
      });
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, anchorRef]);

  if (!isOpen || !menuPosition) {
    return null;
  }

  return createPortal(
    <div
      className="table-action-menu-portal"
      role="menu"
      style={{
        position: "fixed",
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
      }}
    >
      <div
        className="table-action-menu-content"
        onClick={(event) => {
          const target = event.target;

          if (!(target instanceof Element)) {
            return;
          }

          const actionItem = target.closest(
            ".table-action-menu-item, button, a",
          );

          if (actionItem) {
            onClose();
          }
        }}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
};

const HireneuronTableBody = (props) => {
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const actionButtonRefs = useRef({});

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        !event.target.closest(".table-action-toggle") &&
        !event.target.closest(".table-action-menu-portal")
      ) {
        setOpenActionMenu(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const renderCellContent = (row, column, index) => {
    if (column.type === "LIST") {
      return verifyData(row[column.label]) && row[column.label].length > 0
        ? row[column.label].join(", ")
        : "NA";
    }

    if (column.displayFunction) {
      return column.displayFunction(row, index);
    }

    return verifyData(row[column.label]) ? row[column.label] : "NA";
  };

  const isActionColumn = (column) =>
    column != null &&
    (column.action === true ||
      column.actions === true ||
      column.label === "action");

  //for position arranging
  const getClassName = (position) => {
    let className = "";
    if (position !== undefined && position !== null && position !== "") {
      if (position === "center") {
        return className + " text-center";
      } else if (position === "start") {
        return className + " text-start";
      } else {
        return className + ` ${position}`;
      }
    } else {
      return className + " content pt-2";
    }
  };
  const getIds = (row, label) => {
    return "as-" + label.toLowerCase().replaceAll(" ", "-") + "-" + row;
  };
  return (
    <>
      {props.tableData && props.tableData.length > 0 ? (
        props.tableData.map((row, index) => {
          const itemKey = row.id ? `row_${row.id}` : `row_${index}`;
          return (
            <tr
              ref={
                props.reference !== undefined &&
                props.tableData.length === index + 1 &&
                !props.paginationShow
                  ? props.reference
                  : null
              }
              key={itemKey}
            >
              {props.columns
                .filter(
                  (column) =>
                    verifyData(column) &&
                    props.visibleColumns.includes(column.label),
                )
                .map((column, colIndex) => {
                  const columnId = column.id
                    ? `row_${column.id}`
                    : `row_${colIndex}`;
                  const columnValue = processNumericLinks(
                    renderCellContent(row, column, index),
                  );
                  const rowKey = row.id ? `row_${row.id}` : `row_${index}`;
                  const isAction = isActionColumn(column);
                  const actionCount = isAction
                    ? getActionCount(columnValue)
                    : 0;
                  const isDropdownMenu = actionCount > 1;
                  const enhancedColumnValue = isAction
                    ? enhanceActionElements(
                        columnValue,
                        "",
                        isDropdownMenu,
                        () => setOpenActionMenu(null),
                      )
                    : columnValue;
                  return (
                    <td
                      key={`column_${columnId}`}
                      translate="no"
                      className={`${getClassName(column.position)} ${isAction ? "table-action-cell" : ""}`.trim()}
                      id={getIds(index, column.label)}
                    >
                      {isAction ? (
                        actionCount > 1 ? (
                          <>
                            <button
                              ref={(element) => {
                                actionButtonRefs.current[rowKey] = element;
                              }}
                              type="button"
                              className="table-action-toggle"
                              aria-label="Open row actions"
                              aria-expanded={openActionMenu === rowKey}
                              onClick={() =>
                                setOpenActionMenu((current) =>
                                  current === rowKey ? null : rowKey,
                                )
                              }
                            >
                              <i className="fas fa-ellipsis-v" />
                            </button>

                            <ActionMenuPortal
                              isOpen={openActionMenu === rowKey}
                              anchorRef={{
                                current: actionButtonRefs.current[rowKey],
                              }}
                              onClose={() => setOpenActionMenu(null)}
                            >
                              {React.isValidElement(enhancedColumnValue) ? (
                                enhancedColumnValue
                              ) : (
                                <div className="table-action-menu-item text-muted">
                                  {verifyData(enhancedColumnValue)
                                    ? enhancedColumnValue
                                    : "No actions available"}
                                </div>
                              )}
                            </ActionMenuPortal>
                          </>
                        ) : (
                          enhancedColumnValue
                        )
                      ) : (
                        enhancedColumnValue
                      )}
                    </td>
                  );
                })}
            </tr>
          );
        })
      ) : (
        <tr>
          <td colSpan={props.columns.length}>No data available</td>
        </tr>
      )}
      {props.bodySpinner && <Spinner />}
    </>
  );
};

export default HireneuronTableBody;
